import classNames from 'classnames';
import React, { FC } from 'react';
import { useNavigate } from 'react-router';
import { useDrag, useDrop } from 'react-dnd';
import APP_STATUS from '@integrations/pint-appstore-commons/src/enums/APP_STATUS.json';
import Icon from '../common/Icon';
import t from '../../translations';
import { AppId, Editor } from '../common/interfaces';

const transitionToLabelMap = {
    [APP_STATUS.DISABLED]: 'Not enabled for domain',
    [APP_STATUS.ADDED_FOR_DOMAIN_GROUPS]: 'Added for groups',
    [APP_STATUS.ALLOWED_ADDED_USER]: 'Allowed for users',
    [APP_STATUS.ADDED_DOMAIN]: 'Added for all users',
    [APP_STATUS.ALLOWED]: 'Allowed for users',
};

interface ControlCenterItemProps {
    app: Editor;
    fetchStatusForm: (appId: AppId) => void;
    moveEditor: (appId: AppId, index: number) => void;
    findEditor: (appId: AppId) => { editor: any; index: number };
    prioritizationEnabled: boolean;
    submitPriorityUpdate: () => void;
}

const ITEM_TYPE = 'editor';
interface Item {
    appId: AppId;
    originalIndex: number;
}

const ControlCenterItem: FC<ControlCenterItemProps> = ({
    app,
    fetchStatusForm,
    moveEditor,
    findEditor,
    prioritizationEnabled,
    submitPriorityUpdate,
}) => {
    const isAppDisabled = APP_STATUS.DISABLED === app.status;
    const itemNameColumn = classNames('control-center__panel-item-name-column', {
        'control-center__panel-item-name-column--disabled': isAppDisabled,
        'control-center__panel-item-name-column--clickable': true,
    });
    const navigate = useNavigate();
    const originalIndex = findEditor(app.appId).index;
    const [{ isDragging }, drag] = useDrag(
        () => ({
            type: ITEM_TYPE,
            item: {
                appId: app.appId,
                originalIndex,
            },
            collect: (monitor) => ({
                isDragging: monitor.isDragging(),
            }),
            end: (item, monitor) => {
                const { appId: droppedAppId, originalIndex } = item;
                const didDrop = monitor.didDrop();
                if (!didDrop) {
                    moveEditor(droppedAppId, originalIndex);
                }
            },
            canDrag: () => app.status !== APP_STATUS.DISABLED,
        }),
        [app.appId, originalIndex, moveEditor]
    );

    const [, drop] = useDrop(
        () => ({
            accept: ITEM_TYPE,
            hover(item: Item) {
                const { appId: draggedAppId } = item;
                if (draggedAppId !== app.appId) {
                    const { index: overIndex } = findEditor(app.appId);
                    moveEditor(draggedAppId, overIndex);
                }
            },
            canDrop: (/* item, monitor */) => app.status !== APP_STATUS.DISABLED,
            drop(item /* , monitor */) {
                submitPriorityUpdate();

                return item;
            },
        }),
        [findEditor, moveEditor]
    );

    return (
        <div
            className={`control-center__panel-item ${
                prioritizationEnabled ? '' : 'control-center__panel-item--prioritization-disabled'
            }`}
            ref={(node) => {
                if (prioritizationEnabled && app.status !== APP_STATUS.DISABLED) {
                    const dropRef = drop(node);
                    drag(dropRef);
                }
            }}
            style={{ opacity: isDragging ? 0 : 1 }}
        >
            {prioritizationEnabled && <DragDropIcon />}
            <div
                className={itemNameColumn}
                onClick={() => navigate(`/apps/${app.appId}`, { state: { prevPath: '/control-center/' } })}
            >
                <img className="control-center__panel-item-logo" src={app.iconUrl} alt={app.name} />
                <div className="control-center__panel-item-name">{app.name}</div>
            </div>
            <div className="control-center__panel-item-status-column">
                <div className="control-center__panel-item-status">{transitionToLabelMap[app.status]}</div>

                <button
                    type="button"
                    className="sg-button-action control-center__settings-button"
                    onClick={() => fetchStatusForm(app.appId)}
                >
                    <Icon alias="settings" />
                    <span>{t.controlCenter.settings}</span>
                </button>
            </div>
        </div>
    );
};

const DragDropIcon = () => (
    <span className="control-center__panel-item-drag-drop-icon">
        <span className="control-center__panel-item-drag-drop-icon-dot" />
        <span className="control-center__panel-item-drag-drop-icon-dot" />
        <span className="control-center__panel-item-drag-drop-icon-dot" />
        <span className="control-center__panel-item-drag-drop-icon-dot" />
        <span className="control-center__panel-item-drag-drop-icon-dot" />
        <span className="control-center__panel-item-drag-drop-icon-dot" />
    </span>
);

export default ControlCenterItem;
